import * as React from 'react'
import isEqual from 'lodash/isEqual'
import moment, {type Moment} from 'moment-timezone'
import {fromPromise} from 'neverthrow'
import {exhaustive} from '@freckle/exhaustive'
import {maybe, mthen} from '@freckle/maybe'
import type {ParserT} from '@freckle/parser'
import {record, nullable, array, date, oneOf, mapStatic} from '@freckle/parser'
import {useSafeCallback, useSafeEffectExtraDeps} from '@freckle/react-hooks'
import type {
  RetryAfterT,
  OperationLocationT
} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {fancyApiFetchWithRetry} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {toError} from '@freckle/educator-entities/ts/common/helpers/catch-error'
import BugsnagHelper from '@freckle/educator-entities/ts/common/helpers/exception-handlers/bugsnag-helper'
import type {Artifact, ArtifactData} from '@freckle/educator-entities/ts/common/models/artifact'
import {
  artifactParser,
  fetchArtifactData
} from '@freckle/educator-entities/ts/common/models/artifact'

type Props = {
  operationLocation: OperationLocationT
}

type Operation = {
  updatedAt: Moment | undefined | null
  status: OperationStatus
  artifacts: Array<Artifact>
}

export type ArtifactDataState =
  | {
      tag: 'not_started'
    }
  | {
      tag: 'fetching'
    }
  | {
      tag: 'success'
      artifactData: ArtifactData[]
    }
  | {
      tag: 'failed'
    }

export type OperationStatus = 'not_started' | 'running' | 'succeeded' | 'failed'
const allOperationStatuses = [
  'not_started' as const,
  'running' as const,
  'succeeded' as const,
  'failed' as const
]

export const useOperation = ({
  operationLocation
}: Props): {
  operation: Operation | undefined | null
  artifactData: ArtifactDataState
} => {
  const [operation, setOperation] = React.useState<Operation | undefined | null>(null)
  const [retryAfterObj, setRetryAfterObj] = React.useState<
    | {
        retryAfter: RetryAfterT
        lastUpdated: Moment | undefined | null
        attempt: number
      }
    | undefined
    | null
  >(null)
  const [artifactData, setArtifactData] = React.useState<ArtifactDataState>({
    tag: 'not_started'
  })

  const getOperation = useSafeCallback(
    () => async () => {
      const result = await fromPromise(
        fancyApiFetchWithRetry<Operation>(operationLocation, operationParser),
        toError
      )

      if (result.isErr()) {
        console.error(result.error)
        BugsnagHelper.logError(result.error)
        return
      }

      const {response, retryAfter: mRetryAfter} = result.value

      setOperation(response)

      const updateRetryAfter = () =>
        setRetryAfterObj(
          mthen(mRetryAfter, retryAfter => ({
            retryAfter,
            lastUpdated: response.updatedAt,
            attempt: (retryAfterObj?.attempt ?? 0) + 1
          }))
        )

      switch (response.status) {
        case 'not_started':
          updateRetryAfter()
          break
        case 'running':
          updateRetryAfter()
          break
        case 'succeeded':
          setRetryAfterObj(null)
          setArtifactData({tag: 'fetching'})
          try {
            const artifactData = await Promise.all(response.artifacts.map(fetchArtifactData))
            setArtifactData({
              tag: 'success',
              artifactData
            })
          } catch (e) {
            BugsnagHelper.logError(e as Error)
            setArtifactData({tag: 'failed'})
          }
          break
        case 'failed':
          setRetryAfterObj(null)
          break
        default:
          exhaustive(response.status)
      }
    },
    [operationLocation, retryAfterObj?.attempt]
  )

  useSafeEffectExtraDeps(
    ({getOperation}: {getOperation: () => unknown}) => {
      getOperation() // initial get
    },
    [],
    {getOperation}
  )

  useSafeEffectExtraDeps(
    ({
      getOperation,
      retryAfterObj
    }: {
      getOperation: () => unknown
      retryAfterObj:
        | {
            retryAfter: RetryAfterT
            lastUpdated: Moment | undefined | null
            attempt: number
          }
        | null
        | undefined
    }) => {
      const setupRetry = (retryAfter: RetryAfterT): number => {
        switch (retryAfter.tag) {
          case 'delay-seconds':
            const timeoutMs = retryAfter.contents * 1000
            return window.setTimeout(getOperation, timeoutMs)
          case 'http-date':
            throw new Error('HTTP date header unsupported in Operations API')
          default:
            return exhaustive(retryAfter as never)
        }
      }

      return maybe(
        () => undefined, // No cleanup function need if polling is not setup
        ({retryAfter}: {retryAfter: RetryAfterT}) => {
          const timeoutId = setupRetry(retryAfter)
          return () => window.clearTimeout(timeoutId)
        },
        retryAfterObj
      )
    },
    [],
    {
      getOperation,
      retryAfterObj: {value: retryAfterObj, comparator: isEqual}
    }
  )

  return {operation, artifactData}
}

const operationParser: ParserT<Operation> = record({
  updatedAt: nullable(mapStatic(date(), moment)),
  status: oneOf('OperationStatus', allOperationStatuses),
  artifacts: array(artifactParser)
})
